import {
  BOT_SEQUENCE_ALL_CHANNEL_TYPES,
  BOT_SEQUENCE_CONTENT_TEMPLATES,
  BOT_SEQUENCE_CONTENT_TEMPLATE_LABELS,
  BOT_SEQUENCE_DEFAULT_BODY_LOCALE,
  BOT_SEQUENCE_STANDARD_DAYS,
  BotSequenceConfigStep,
  BotSequenceConfigStepContent,
  BotSequenceConfigStepDelivery,
  BotSequenceConfigStepEmail,
  BotSequenceContentTemplate,
  BotSequenceCopyDefaults,
  BotSequenceDeliveryChannel,
  BotSequenceStandardDay,
  BotSequenceType,
  Channel,
  defaultCopyFor
} from "@swivl-mono/types";

import { SEQUENCE_TYPE_META } from "./sequenceTypeMeta";

/**
 * Pure helpers over a ladder draft (ENG-3216). The ladder cascades as ONE unit, and a
 * saved day's number is fixed (decision 37) — so these add, remove and edit days but never
 * renumber a saved one.
 */

export function mintStepKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `step-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function sortLadder(ladder: BotSequenceConfigStep[]): BotSequenceConfigStep[] {
  return [...ladder].sort((left, right) => left.offsetDays - right.offsetDays);
}

export type LadderEdit = { ok: true; ladder: BotSequenceConfigStep[] } | { ok: false; error: string };

/** What a brand-new day may switch on besides the text message (decision 52). */
export interface NewDayOptions {
  /** True when the FMS behind this scope can take tenant notes — the note is on by default then. */
  fmsNoteAllowed: boolean;
  /** True when the org has a Mandrill App for email. */
  emailAllowed: boolean;
}

function usesTemplate(step: BotSequenceConfigStep, template: BotSequenceContentTemplate): boolean {
  return step.email?.template === template || (step.deliveries ?? []).some((d) => d.content.mode === "generated" && d.content.template === template);
}

/** The nearest earlier day already using the template, so edits carry forward. */
export function priorTemplateStep(
  ladder: BotSequenceConfigStep[],
  offsetDays: number,
  template: BotSequenceContentTemplate
): BotSequenceConfigStep | undefined {
  return sortLadder(ladder)
    .filter((step) => step.offsetDays < offsetDays && usesTemplate(step, template))
    .pop();
}

/** Chosen template's guidance: the prior day's edits, else the default. */
export function templateGuidance(
  type: BotSequenceType,
  offsetDays: number,
  channel: BotSequenceDeliveryChannel,
  template?: BotSequenceContentTemplate,
  prior?: BotSequenceConfigStep
): string {
  const content = prior?.deliveries?.find((delivery) => delivery.channel === channel)?.content;
  const copied = template && content?.mode === "generated" && content.template === template ? content.guidance : undefined;
  return copied?.trim() ? copied : defaultCopyFor(type, offsetDays, template).guidance;
}

/** Chosen template's email: the prior day's edits, else the default. */
export function templateEmail(
  type: BotSequenceType,
  offsetDays: number,
  template?: BotSequenceContentTemplate,
  prior?: BotSequenceConfigStep
): Pick<BotSequenceConfigStepEmail, "subject" | "body"> {
  const email = prior?.email;
  if (template && email?.template === template && email.body.trim()) return { subject: email.subject, body: email.body };
  const defaults = defaultCopyFor(type, offsetDays, template);
  return { subject: defaults.emailSubject, body: defaults.emailBody };
}

/** A voicemail follows a template switch only while it still holds the earlier default wording. */
export function voicemailAfterTemplate(
  voicemail: BotSequenceConfigStepDelivery["voicemail"],
  defaultBefore: string,
  defaultAfter: string
): BotSequenceConfigStepDelivery["voicemail"] {
  if (!voicemail || !bodiesAreDefault(voicemail.bodies, defaultBefore)) return voicemail;
  return { ...voicemail, bodies: { [BOT_SEQUENCE_DEFAULT_BODY_LOCALE]: defaultAfter } };
}

/** True when the bodies hold only the default English wording. */
export function bodiesAreDefault(bodies: Record<string, string>, defaultText: string): boolean {
  return Object.keys(bodies).length === 1 && bodies[BOT_SEQUENCE_DEFAULT_BODY_LOCALE] === defaultText;
}

/** A delivery's starting state: exact-wording text, or a voicemail-on call. */
export function defaultDelivery(
  channel: BotSequenceDeliveryChannel,
  copy: BotSequenceCopyDefaults,
  template?: BotSequenceContentTemplate
): BotSequenceConfigStepDelivery {
  const generated: BotSequenceConfigStepContent = { mode: "generated", guidance: copy.guidance, ...(template ? { template } : {}) };
  if (channel === Channel.VOICE) {
    return { channel, content: generated, voicemail: { enabled: true, bodies: { [BOT_SEQUENCE_DEFAULT_BODY_LOCALE]: copy.voicemail } } };
  }
  return { channel, content: template ? generated : { mode: "verbatim", bodies: { [BOT_SEQUENCE_DEFAULT_BODY_LOCALE]: copy.sms } } };
}

/** A new day starts filled in; all-channel types add call and email. */
export function newStep(
  offsetDays: number,
  type: BotSequenceType,
  options: NewDayOptions,
  template?: BotSequenceContentTemplate
): BotSequenceConfigStep {
  const copy = defaultCopyFor(type, offsetDays, template);
  const allChannels = BOT_SEQUENCE_ALL_CHANNEL_TYPES.includes(type) || Boolean(template);
  const channels: BotSequenceDeliveryChannel[] = allChannels ? [Channel.SMS, Channel.VOICE] : [Channel.SMS];
  return {
    key: mintStepKey(),
    offsetDays,
    enabled: true,
    deliveries: channels.map((channel) => defaultDelivery(channel, copy, template)),
    ...(allChannels && options.emailAllowed
      ? { email: { enabled: true, subject: copy.emailSubject, body: copy.emailBody, ...(template ? { template } : {}) } }
      : {}),
    ...(options.fmsNoteAllowed ? { fmsNote: { enabled: true, text: copy.fmsNote } } : {})
  };
}

export function addDay(ladder: BotSequenceConfigStep[], offsetDays: number, type: BotSequenceType, options: NewDayOptions): LadderEdit {
  if (!Number.isInteger(offsetDays) || offsetDays < 0) {
    return { ok: false, error: "Enter a whole number of days, 0 or more." };
  }
  if (ladder.some((step) => step.offsetDays === offsetDays)) {
    return { ok: false, error: `Day ${offsetDays} already exists.` };
  }
  return { ok: true, ladder: sortLadder([...ladder, newStep(offsetDays, type, options)]) };
}

/** The type's standard starting days, each fully filled in (Round 2, R4). */
export function seedStandardDays(type: BotSequenceType, options: NewDayOptions): BotSequenceConfigStep[] {
  return BOT_SEQUENCE_STANDARD_DAYS[type].map(({ offsetDays, template }) => newStep(offsetDays, type, options, template));
}

export function removeDay(ladder: BotSequenceConfigStep[], key: string): BotSequenceConfigStep[] {
  return ladder.filter((step) => step.key !== key);
}

export function updateDay(ladder: BotSequenceConfigStep[], key: string, patch: Partial<BotSequenceConfigStep>): BotSequenceConfigStep[] {
  return sortLadder(ladder.map((step) => (step.key === key ? { ...step, ...patch } : step)));
}

/**
 * A deep copy for "Customize for this scope": same offsets, same keys (keys are UI identity
 * only; the cursor is offset-based, so tenants mid-sequence are unaffected).
 */
export function copyLadderForScope(ladder: BotSequenceConfigStep[]): BotSequenceConfigStep[] {
  return JSON.parse(JSON.stringify(ladder)) as BotSequenceConfigStep[];
}

/** The chips a collapsed day row shows. */
/**
 * The chips a day row shows. `startingScriptName` is the name of the day's starting script when it
 * is in force at this scope (ENG-3403); a slug that no longer resolves still shows a chip so the
 * row says one is set.
 */
export function summarizeDay(step: BotSequenceConfigStep, type: BotSequenceType, startingScriptName?: string): string[] {
  const chips: string[] = [];
  const deliveries = step.deliveries ?? [{ channel: Channel.SMS, content: { mode: "generated" } }];
  for (const delivery of deliveries) {
    if (delivery.channel === Channel.SMS) chips.push("SMS");
    if (delivery.channel === Channel.VOICE) {
      chips.push("Voice");
      if (delivery.voicemail?.enabled) chips.push("Voicemail");
    }
  }
  if (step.email?.enabled) chips.push("Email");
  for (const template of BOT_SEQUENCE_CONTENT_TEMPLATES) {
    if (usesTemplate(step, template)) chips.push(BOT_SEQUENCE_CONTENT_TEMPLATE_LABELS[template]);
  }
  if (step.fmsNote?.enabled) chips.push("FMS note");
  if (step.sendASAP && SEQUENCE_TYPE_META[type].offersSendImmediately) chips.push("Immediately");
  if (step.deferrable === false) chips.push("Always send");
  if (step.callScriptSlug) chips.push(startingScriptName ? `Script: ${startingScriptName}` : "Starting script");
  return chips;
}

/** Ids of the days in `saved` — those whose number is fixed in the editor. */
export function savedStepKeys(saved: BotSequenceConfigStep[] | null): Set<string> {
  return new Set((saved ?? []).map((step) => step.key));
}
